import {SpinnerModule} from '../../../spinner/spinner.module';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser, NgForOf, NgIf } from '@angular/common';
import { FormsModule, UntypedFormGroup } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { RxFormBuilder, RxwebValidators } from '@rxweb/reactive-form-validators';
import { CheckboxModule } from 'primeng/checkbox';
import { SkeletonModule } from 'primeng/skeleton';
import { ModalInterface } from '../../modal.interface';
import { ModalStateEvent } from '../../modal-state-event';
import { ModalState } from '../../modal-state.enum';
import { ProjectViewModel } from '../../../../features/user/garage/project.view-model';
import { DescriptionPostViewModel } from '../../../../features/user/project/about/details/description-post.view-model';
import { PipesModule } from '../../../../core/pipes/pipes.module';
import { MediaObjectDataProviderService } from './media-object.data-provider.service';
import { MediaObjectViewModel } from '../../../../core/models/media-object.view-model';
import { MediaObjectInterface } from '../../../../core/interfaces/media-object.interface';
import { InstagramMediaItemApiModel } from '../../../../api/models/instagram-media-item.api-model';
import { VirtualizationModule } from '../../../virtualization/virtualization.module';

interface ProjectGalleryPlaceholderItem extends MediaObjectInterface {
  isPlaceholder: true;
  placeholderKey: string;
  selected: false;
  type: 'IMAGE';
  mimeType: string;
  dimensions: number[];
  hyperlinks: [];
}

type ProjectGalleryMediaItem = MediaObjectInterface | ProjectGalleryPlaceholderItem;
type InstagramChoiceItem = InstagramMediaItemApiModel & { selected?: boolean };
type MediaChoiceItem = ProjectGalleryMediaItem | InstagramChoiceItem;

@Component({
  selector: 'app-media-choice',
  templateUrl: './media-choice.component.html',
  styleUrls: ['./media-choice.component.scss'],
  imports: [
    SpinnerModule,
    NgForOf,
    PipesModule,
    NgIf,
    CheckboxModule,
    FormsModule,
    SkeletonModule,
    VirtualizationModule,
  ],
  standalone: true,
  providers: [MediaObjectDataProviderService],
})
export class MediaChoiceComponent implements ModalInterface, OnInit, AfterViewInit, OnChanges {
  data: any;
  subject: Subject<ModalStateEvent>;
  state: Observable<ModalStateEvent>;
  form: UntypedFormGroup;

  @Input() public projectVM: ProjectViewModel;
  @Input() public descriptionPost: DescriptionPostViewModel;
  @Input() public reference: HTMLElement;
  @Input() public selectedMedia: MediaObjectInterface[] = [];
  @Input() public selectedInstagramMediaIds: string[] = [];
  @Input() public instagramItems: InstagramMediaItemApiModel[] = [];
  @Input() public submitting = false;
  @Input() public mode: 'project-gallery' | 'instagram' = 'project-gallery';
  @Output() onAdd: EventEmitter<DescriptionPostViewModel> = new EventEmitter();
  @ViewChild('container', { static: false }) container?: ElementRef<HTMLElement>;

  public items: MediaChoiceItem[] = [];
  public containerWidth = 0;
  public page = 1;
  public loadingMore = false;
  public endOfResults = false;
  private totalItems = 9;

  @HostListener('window:resize')
  onResize(): void {
    this.setContainerWidth();
    if (this.mode === 'project-gallery' && this.hasOnlyPlaceholderItems()) {
      this.items = this.createProjectGalleryPlaceholders();
    }
  }

  constructor(
    @Inject(PLATFORM_ID) public platformId: Object,
    private readonly fb: RxFormBuilder,
    private readonly mediaObjectDPS: MediaObjectDataProviderService,
  ) {
  }

  ngOnInit(): void {
    this.syncModeItems();
    if (this.mode === 'instagram') {
      return;
    }

    this.prepareProjectGalleryLoading();
    this.loadProjectGallery(1);
  }

  ngAfterViewInit(): void {
    this.setContainerWidth();
    if (this.mode === 'project-gallery' && this.hasOnlyPlaceholderItems()) {
      this.items = this.createProjectGalleryPlaceholders();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['instagramItems'] || changes['selectedInstagramMediaIds'] || changes['mode']) {
      this.syncModeItems();
    }

    if ((changes['projectVM'] || changes['mode']) && this.mode === 'project-gallery' && this.projectVM?.slug) {
      this.prepareProjectGalleryLoading();
      this.loadProjectGallery(1);
    }
  }

  requestNextPage(): void {
    if (this.mode === 'instagram') {
      return;
    }

    if (this.endOfResults || (this.totalItems && this.items.length >= this.totalItems)) {
      return;
    }

    this.loadProjectGallery(this.page);
  }

  private createForm(): void {
    this.form = this.fb.group({
      content: [null, [RxwebValidators.minLength({ value: 3 })]],
    });

    if (this.descriptionPost) {
      this.form.get('content')?.setValue(this.descriptionPost.content);
    }

    this.form.valueChanges.subscribe(() => {
      if (this.form.valid) {
        this.subject.next(new ModalStateEvent(ModalState.VALID));
      }
    });
  }

  setContainerWidth(): void {
    this.containerWidth = this.container?.nativeElement?.clientWidth || 0;
  }

  public readonly isPlatformBrowser = isPlatformBrowser;

  public toggleSelectItem(item: any): void {
    if (this.submitting) {
      return;
    }

    this.setSelectItem(item, !item.selected);
  }

  public setSelectItem(item: any, selected: boolean): void {
    if (this.submitting) {
      return;
    }
    if (this.isPlaceholderItem(item)) {
      return;
    }

    const key = this.getSelectionKey(item);
    const selectedItem = this.items.find((entry: any) => this.getSelectionKey(entry) === key);
    if (!selectedItem || this.isPlaceholderItem(selectedItem)) {
      return;
    }

    selectedItem.selected = selected;
    item.selected = selected;
    if (this.mode === 'instagram') {
      this.selectedInstagramMediaIds = this.items
        .filter((entry: any) => entry.selected)
        .map((entry: any) => entry.instagramMediaId);
      return;
    }

    this.selectedMedia = this.items.filter((entry: any) => !this.isPlaceholderItem(entry) && entry.selected) as MediaObjectInterface[];
  }

  public getMediaObject(item: ProjectGalleryMediaItem): MediaObjectInterface {
    return item as MediaObjectInterface;
  }

  public preSelectItems(): void {
    if (this.mode === 'instagram') {
      this.items.forEach((item: any) => {
        item.selected = this.selectedInstagramMediaIds.includes(item.instagramMediaId);
      });
      return;
    }

    this.selectedMedia.forEach((selectedMedia) => {
      const itemToSelect = this.items.find((item: any) => !this.isPlaceholderItem(item) && selectedMedia['@id'] === item['@id']);
      if (itemToSelect) {
        itemToSelect.selected = true;
      }
    });
  }

  public reset(): void {
    this.selectedMedia = [];
    this.selectedInstagramMediaIds = [];
    this.items.forEach((item: any) => item.selected = false);
    this.items = [...this.items];
  }

  public selectAll(): void {
    this.items.forEach((item: any) => {
      if (!this.isPlaceholderItem(item)) {
        item.selected = true;
      }
    });
    if (this.mode === 'instagram') {
      this.selectedInstagramMediaIds = this.items.map((entry: any) => entry.instagramMediaId);
    } else {
      this.selectedMedia = this.items.filter((entry: any) => !this.isPlaceholderItem(entry)) as MediaObjectInterface[];
    }
    this.items = [...this.items];
  }

  public getThumbnailSrc(item: any): string | null {
    if (this.mode === 'instagram') {
      return item.thumbnailUrl || item.mediaUrl || null;
    }

    return null;
  }

  public isImageItem(item: any): boolean {
    if (this.mode === 'instagram') {
      return item.mediaType === 'IMAGE' || item.mediaType === 'CAROUSEL_ALBUM';
    }

    return item.type === 'IMAGE';
  }

  public isVideoItem(item: any): boolean {
    if (this.mode === 'instagram') {
      return item.mediaType === 'VIDEO';
    }

    return item.type === 'VIDEO';
  }

  public get projectGalleryFrame(): number[][] {
    return this.getProjectGalleryWidth() < 1024
      ? [[1, 2, 3], [4, 5, 6]]
      : [[1, 2, 3, 4], [5, 6, 7, 8]];
  }

  public trackByMediaItem(index: number, item: any): string {
    if (item?.isPlaceholder === true) {
      return item.placeholderKey || `media-placeholder:${index}`;
    }

    return item?.instagramMediaId
      || item?.['@id']
      || item?.id
      || `media:${index}`;
  }

  public isPlaceholderItem(item: any): item is ProjectGalleryPlaceholderItem {
    return item?.isPlaceholder === true;
  }

  public get isInitialProjectGalleryLoading(): boolean {
    return this.mode === 'project-gallery' && this.items.some((item) => this.isPlaceholderItem(item));
  }

  public get hasLoadedProjectGalleryItems(): boolean {
    return this.mode === 'project-gallery' && this.items.some((item) => !this.isPlaceholderItem(item));
  }

  private getSelectionKey(item: any): string {
    return this.mode === 'instagram' ? item.instagramMediaId : item['@id'];
  }

  private syncModeItems(): void {
    if (this.mode !== 'instagram') {
      return;
    }

    this.items = this.instagramItems.map((item: InstagramMediaItemApiModel) => ({
      ...item,
      selected: this.selectedInstagramMediaIds.includes(item.instagramMediaId),
    })) as InstagramChoiceItem[];
  }

  private prepareProjectGalleryLoading(): void {
    this.page = 1;
    this.endOfResults = false;
    this.totalItems = 9;
    this.items = this.createProjectGalleryPlaceholders();
  }

  private loadProjectGallery(page: number): void {
    if (!this.projectVM?.slug || this.mode === 'instagram' || this.loadingMore) {
      return;
    }

    if (page > 1) {
      this.items = [
        ...this.stripProjectGalleryPlaceholders(this.items),
        ...this.createProjectGalleryPlaceholders(this.getProjectGalleryAppendPlaceholderCount(), `project-gallery-append:${page}`),
      ];
    }

    this.loadingMore = true;
    this.mediaObjectDPS.getMediaObjectSubresourceCollection(`projects/${this.projectVM.slug}`, page)
      .pipe(tap(() => this.totalItems = this.mediaObjectDPS.totalItems))
      .subscribe((mediaObjectVMs: MediaObjectViewModel[]) => {
        if (!mediaObjectVMs.length) {
          this.endOfResults = true;
        }

        const existingItems = this.stripProjectGalleryPlaceholders(this.items);
        this.items = page === 1
          ? [...mediaObjectVMs]
          : [...existingItems, ...mediaObjectVMs];
        this.page = page + 1;
        if (this.totalItems && this.items.length >= this.totalItems) {
          this.endOfResults = true;
        }
        this.preSelectItems();
        this.loadingMore = false;
      }, () => {
        if (page === 1) {
          this.items = [];
        } else {
          this.items = this.stripProjectGalleryPlaceholders(this.items);
        }
        this.loadingMore = false;
      });
  }

  private createProjectGalleryPlaceholders(
    count: number = this.getProjectGalleryPlaceholderCount(),
    prefix: string = 'project-gallery-placeholder',
  ): ProjectGalleryPlaceholderItem[] {
    return Array.from({ length: count }, (_, index) => ({
      isPlaceholder: true,
      placeholderKey: `${prefix}:${index}`,
      selected: false,
      type: 'IMAGE',
      mimeType: '',
      dimensions: [],
      hyperlinks: [],
    }));
  }

  private getProjectGalleryPlaceholderCount(): number {
    return this.projectGalleryFrame.reduce((count, row) => count + row.length, 0);
  }

  private getProjectGalleryAppendPlaceholderCount(): number {
    return this.projectGalleryFrame[0]?.length || 0;
  }

  private hasOnlyPlaceholderItems(): boolean {
    return this.items.length > 0 && this.items.every((item) => this.isPlaceholderItem(item));
  }

  private stripProjectGalleryPlaceholders(items: MediaChoiceItem[]): MediaChoiceItem[] {
    return items.filter((item) => !this.isPlaceholderItem(item));
  }

  private getProjectGalleryWidth(): number {
    if (this.containerWidth > 0) {
      return this.containerWidth;
    }

    if (isPlatformBrowser(this.platformId)) {
      return window.innerWidth || 0;
    }

    return 0;
  }
}
